import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  Edit2,
  Loader2,
  Plus,
  Search,
  X,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";

const LIST_QUERY = ["translations", "list"] as const;
const PAGE_SIZE = 15;

export default function TranslationsList() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const query = useQuery({
    queryKey: [...LIST_QUERY, { page, search }],
    queryFn: () => api.translations.list({ page, limit: PAGE_SIZE, search }),
  });

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const list = query.data?.translations ?? [];
  const totalPages = query.data?.totalPages ?? 1;
  const total = query.data?.total ?? 0;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Translations</h1>
        <p className="text-sm text-muted-foreground">
          Hebrew translations for stocks and ETFs.
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by ticker, stock ID, or name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-72 pl-10"
            />
          </div>
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" /> Add translation
        </Button>
      </div>

      {query.isError && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof ApiError
            ? query.error.message
            : "Failed to load translations"}
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="font-semibold">
            Asset translations {!query.isLoading && `(${total})`}
          </h2>
        </div>

        {query.isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            {search
              ? "No translations match your search."
              : "No translations found."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticker</TableHead>
                <TableHead>Stock ID</TableHead>
                <TableHead>Company Name (HE)</TableHead>
                <TableHead>Factors</TableHead>
                <TableHead className="w-16 text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((t) => (
                <TableRow key={t.stockId}>
                  <TableCell className="font-mono text-sm font-semibold">
                    {t.ticker ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {t.stockId}
                  </TableCell>
                  <TableCell className="text-sm" dir="auto">
                    {t.companyName || (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {t.keypointCount > 0 ? (
                      <Badge variant="secondary">{t.keypointCount}</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">0</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      asChild
                      variant="ghost"
                      size="icon"
                      title="Edit"
                    >
                      <Link to={`/dashboard/translations/${t.stockId}`}>
                        <Edit2 className="h-4 w-4" />
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-6 py-3">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <CreateTranslationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: LIST_QUERY });
        }}
      />
    </div>
  );
}

function CreateTranslationDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const [stockId, setStockId] = useState("");
  const [companyName, setCompanyName] = useState("");

  const create = useMutation({
    mutationFn: () =>
      api.translations.create({
        stockId: parseInt(stockId, 10),
        companyName,
      }),
    onSuccess: () => {
      toast.success("Translation created");
      setStockId("");
      setCompanyName("");
      onCreated();
      onOpenChange(false);
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiError ? e.message : "Failed to create translation"
      ),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add asset translation</DialogTitle>
          <DialogDescription>
            Create a new Hebrew translation row for a stock or ETF.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="stockId">Stock ID</Label>
            <Input
              id="stockId"
              type="number"
              required
              value={stockId}
              onChange={(e) => setStockId(e.target.value)}
              placeholder="e.g., 7277"
              className="font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="companyName">Company name (Hebrew)</Label>
            <Input
              id="companyName"
              dir="rtl"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="שם החברה בעברית"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" /> Cancel
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
